import { assign } from 'xstate5';

import {
  checkIfInStock,
  checkIfModificationGroupNotEmpty,
  extendModifierGroup,
  extractModificationsLimits,
  generateModificationsList,
  generateModificationsMap,
  generatePairedModifierGroupMaps,
  mergePairedModifierGroups,
} from '../../helpers';
import { type Context, type CustomizationMachineAction } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates derived modifications based on the provided product and optional line item.
 */
export const generateModifications: CustomizationMachineAction = assign((args) => {
  const { product, productLineItem, isUpsellPricingEnabled } = args.context;

  if (!product) return {};

  const { ingredients } = product;

  const modifierGroups = product.modifierGroups.map((group) => ({
    ...group,
    groupRole: null as string | null,
  }));

  const mergedModifierGroups = isUpsellPricingEnabled
    ? mergePairedModifierGroups(modifierGroups).mergedGroups
    : modifierGroups;

  const initialIngredients = productLineItem?.ingredients ?? ingredients;
  const quantity = productLineItem?.quantity ?? 1;

  const { allModificationsMap, includedModificationsMap, extrasModificationsMap } =
    generateModificationsMap(modifierGroups);

  const modificationGroups = mergedModifierGroups
    .filter(checkIfModificationGroupNotEmpty)
    .map(extendModifierGroup);
  const initialModifications = generateModificationsList(ingredients, allModificationsMap);
  const initialAndAddedModifications = generateModificationsList(
    initialIngredients,
    allModificationsMap,
  );
  const modificationLimits = extractModificationsLimits(modifierGroups);
  const activeModifications = initialAndAddedModifications.modificationsList.filter(checkIfInStock);

  const { includedModifierGroupsByExtrasName, extrasModifierGroupsByIncludedName } =
    generatePairedModifierGroupMaps(modifierGroups);

  // ─────────────────────────────────────────────────────────────────────

  const modifications: Context['modifications'] = {
    initial: initialModifications.modificationsList,
    active: activeModifications,
    additions: [],
    removals: [],
    substitutions: [],
  };

  return {
    allModificationsMap,
    includedModificationsMap,
    extrasModificationsMap,
    includedModifierGroupsByExtrasName,
    extrasModifierGroupsByIncludedName,
    modificationGroups,
    modifications,
    modificationLimits,
    quantity,
    customName: productLineItem?.customName ?? undefined,

    ...(productLineItem?.mixedDressingDetails
      ? { mixedDressingDetails: productLineItem.mixedDressingDetails }
      : {}),
  };
});
